import { useAuth } from '@ahm/react-auth';
import { Box, Group, rem, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useTranslations } from 'next-intl';
import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  DetectMaxCodSource,
  useDetectCODState,
  useDetectMaxCod,
  VerifyState,
  VerifyStatus,
  type UserWithEKYC,
} from '../../hooks/use-detect-cod-state';
import CaretRightIcon from '@/assets/icons/caret-right-icon.svg';
import InfoBlueIcon from '@/assets/icons/info-blue-icon.svg';
import WarningDiamondIcon from '@/assets/icons/warning-diamond-icon.svg';
import { USER_TYPE, VERIFY_ACCOUNT_STATUS } from '@/lib/constants';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

const CodAlert: React.FC = () => {
  const tBooking = useTranslations('booking');
  const { session } = useAuth();
  const user = session?.user;
  const { selectedService } = useBookingStore((state) => state);

  const { control } = useFormContext();

  const { state: codState, disabled: codDisabled } = useDetectCODState(
    user as UserWithEKYC
  );

  const codValue = useWatch({ control, name: 'cod' });
  const [debouncedCod] = useDebouncedValue(codValue, 300);

  const detectMaxCodResult = useDetectMaxCod(
    user!,
    selectedService!,
    debouncedCod
  );
  const detectMaxCodSource = detectMaxCodResult?.source;

  const getAlertMessage = (
    codState: string,
    user: UserWithEKYC | undefined
  ) => {
    switch (codState) {
      case VerifyState.VERIFY_INFO:
        return tBooking('cod_alert_messages.verify_info');
      case VerifyState.IDENTIFICATION:
        return getIdentificationMessage(user);
      case VerifyState.VERIFY_SME_CORPS:
        return getUpgradeAccMessage(user);
      case VerifyStatus.FAIL:
        return getFailMessage(user);
      case VerifyStatus.VERIFYING:
        return getFailMessage(user);
      case VerifyStatus.SUCCESS:
        return getUpgradeAccMessage(user);
      default:
        return getFailMessage(user);
    }
  };

  const getIdentificationMessage = (user: UserWithEKYC | undefined) => {
    if (!user) return '';

    const { user_type, verify_status } = user;

    if (user_type === USER_TYPE.INDIVIDUAL) {
      return tBooking('cod_alert_messages.identification_individual');
    }

    // RULE: if user smes or corporation and account is verifying, show the same message
    if (verify_status === VERIFY_ACCOUNT_STATUS.SUBMITTED) {
      return tBooking('cod_alert_messages.identification_individual');
    }

    if (user_type === USER_TYPE.SME) {
      return tBooking('cod_alert_messages.identification_sme');
    }

    if (user_type === USER_TYPE.CORPORATION) {
      return tBooking('cod_alert_messages.identification_corporation');
    }

    return '';
  };

  const getUpgradeAccMessage = (user: UserWithEKYC | undefined) => {
    if (debouncedCod && detectMaxCodSource === DetectMaxCodSource.USER) {
      if (user?.user_type === USER_TYPE.INDIVIDUAL) {
        return tBooking('cod_alert_messages.upgrade_account_individual');
      }
      if (user?.user_type === USER_TYPE.SME) {
        return tBooking('cod_alert_messages.upgrade_account_sme');
      }
    }
    return '';
  };

  const getFailMessage = (user: UserWithEKYC | undefined) => {
    if (debouncedCod && detectMaxCodSource === DetectMaxCodSource.USER) {
      if (user?.user_type === USER_TYPE.SME) {
        return tBooking('cod_alert_messages.fail_sme');
      }
      if (user?.user_type === USER_TYPE.CORPORATION) {
        return tBooking('cod_alert_messages.fail_corporation');
      }
    }
    return '';
  };

  const getAlertColorAndIcon = (codDisabled: boolean) => {
    if (codDisabled) {
      return {
        color: 'var(--mantine-color-blue-1)',
        icon: <InfoBlueIcon size={16} />,
      };
    }
    return {
      color: 'var(--mantine-color-yellow-1)',
      icon: <WarningDiamondIcon size={16} />,
    };
  };

  const { alertMessage, alertColor, alertIcon } = useMemo(() => {
    const message = getAlertMessage(codState, user);
    const { color, icon } = getAlertColorAndIcon(codDisabled);

    return { alertMessage: message, alertColor: color, alertIcon: icon };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codState, user, debouncedCod, detectMaxCodSource]);

  if (!user || !alertMessage) {
    return null;
  }

  const handleRedirectToVerify = () => {
    const profileUrl = `${process.env.NEXT_PUBLIC_APP_V1_URI}/profile`;
    window.open(profileUrl, '_blank');
  };

  return (
    <Group
      p={rem(12)}
      bg={alertColor}
      c={'neutral.7'}
      wrap="nowrap"
      align="center"
      style={{
        borderRadius: rem(4),
        cursor: 'pointer',
      }}
      onClick={handleRedirectToVerify}
    >
      <Group gap={rem(8)} align="center" wrap="nowrap">
        <Box flex="none">{alertIcon}</Box>
        <Text size="sm" fw={700} flex={1}>
          {alertMessage}
        </Text>
      </Group>

      <CaretRightIcon
        size={16}
        style={{
          flex: 'none',
        }}
      />
    </Group>
  );
};

export default CodAlert;
